import { App } from "vue";
import { createI18n, LocaleMessages, VueMessageType } from "vue-i18n";
import type { I18n, I18nOptions } from "vue-i18n";

import { useUserInfoStore } from "@/stores/user-info";

function formatLocaleToFileName(locale: string) {
  return locale === "Chinese_yy" ? "chineseYY" : locale.toLocaleLowerCase();
}

// 需要等客户端返回用户信息
async function createI18nOptions(): Promise<I18nOptions> {
  const BASE_LANG = "English";

  const vLang = new URL(window.location.href).searchParams.get("vLang");

  const appLang = vLang || useUserInfoStore().getUserInfo.appLang || BASE_LANG;
  const supportLocales = [BASE_LANG];

  if (appLang !== BASE_LANG) {
    supportLocales.push(appLang);
  }

  const langFileObject: Record<string, LocaleMessages<VueMessageType>> = {};

  for (const locale of supportLocales) {
    const fileName = formatLocaleToFileName(locale);
    // lazy loading
    try {
      const module = (await import(`./lang/${fileName}.ts`)) as Record<
        string,
        Record<string, string>
      >;
      langFileObject[locale] = module.default;
    } catch (error) {
      console.log(`${fileName} not found: ${error}`);
    }
  }

  return {
    locale: appLang, // 设置默认语言,设置为从用户信息中获取
    fallbackLocale: BASE_LANG,
    legacy: false,
    globalInjection: true,
    messages: {
      ...langFileObject
    }
  };
}

/**
 * 动态加载语言文件
 * @param locale 界面语言 appLang
 */
export async function loadLocaleMessage(locale: string) {
  const fileName = formatLocaleToFileName(locale);
  try {
    const messages = (await import(`./lang/${fileName}.ts`)) as Record<
      string,
      Record<string, string>
    >;
    i18n.global.setLocaleMessage(locale, messages.default);
  } catch (error) {
    console.log(`${fileName} not found: ${error}`);
  }
}

async function createI18nInstance(): Promise<I18n> {
  const option = await createI18nOptions();
  return createI18n(option);
}

// setup i18n instance with glob
export async function setupI18n(app: App) {
  i18n = await createI18nInstance();
  app.use(i18n);
}

export let i18n: I18n;
